/** Board view state ⇄ URL ⇄ API query. Every filter, the sort, expanded rows and the collapsed group are in the
 * URL so a bot can deep-link any board view. */
import { flagParam, intParam, type Patch } from "../lib/url";

export const RENDER_STATUSES = ["queued", "running", "done", "failed", "cancelled", "stale"] as const;
export const QA_CATEGORIES = [
  "no_errors", "bad_timing", "wrong_canvas_position", "face_cropped", "caption_stack", "audio_issue", "duplicate",
  "off_brand", "wrong_lyrics", "visual_glitch", "other",
] as const;

export type BoardParams = {
  q?: string;
  artist_id?: number;
  sort?: string;
  desc: boolean;
  show_all: boolean;
  status?: string;
  qa_category?: string;
  unreviewed: boolean;
  open: number[];
  collapsed: boolean;
};

export function parseBoardParams(sp: URLSearchParams): BoardParams {
  const open = (sp.get("open") ?? "")
    .split(",")
    .map((x) => intParam(x.trim()))
    .filter((x): x is number => x !== undefined);
  return {
    q: sp.get("q") || undefined,
    artist_id: intParam(sp.get("artist_id")),
    sort: sp.get("sort") || undefined,
    desc: sp.get("desc") !== "0",
    show_all: flagParam(sp.get("show_all")),
    status: sp.get("status") || undefined,
    qa_category: sp.get("qa_category") || undefined,
    unreviewed: flagParam(sp.get("unreviewed")),
    open: [...new Set(open)],
    collapsed: flagParam(sp.get("collapsed")),
  };
}

/** The URL patch that represents p (inverse of parseBoardParams for every field). */
export function boardParamsToPatch(p: BoardParams): Patch {
  return {
    q: p.q,
    artist_id: p.artist_id,
    sort: p.sort,
    desc: p.sort && !p.desc ? "0" : undefined,
    show_all: p.show_all,
    status: p.status,
    qa_category: p.qa_category,
    unreviewed: p.unreviewed,
    open: p.open.length ? p.open.join(",") : undefined,
    collapsed: p.collapsed,
  };
}

export type BoardApiQuery = {
  q?: string;
  artist_id?: number;
  sort?: string;
  desc?: boolean;
  show_all?: boolean;
  status?: string;
  qa_category?: string;
  unreviewed?: string;
};

/** Query for GET /api/board/{group}; render filters are extra query params the server passes to the lister. */
export function boardApiQuery(group: string, p: BoardParams): BoardApiQuery {
  const q: BoardApiQuery = {};
  if (p.q) q.q = p.q;
  if (p.artist_id !== undefined) q.artist_id = p.artist_id;
  if (p.sort) {
    q.sort = p.sort;
    q.desc = p.desc;
  }
  if (p.show_all) q.show_all = true;
  if (group === "renders") {
    if (p.status) q.status = p.status;
    if (p.qa_category) q.qa_category = p.qa_category;
    if (p.unreviewed) q.unreviewed = "1";
  }
  return q;
}

/** Params for GET /api/export/board.md (same view as the screen). */
export function boardMdParams(group: string, p: BoardParams): Record<string, string | number | undefined> {
  const q = boardApiQuery(group, p);
  return {
    group,
    q: q.q,
    artist_id: q.artist_id,
    show_all: q.show_all ? "1" : undefined,
    status: q.status,
    qa_category: q.qa_category,
    unreviewed: q.unreviewed,
  };
}

/** Clicking a column header: a new column sorts descending first; the same column flips direction. */
export function nextSort(p: BoardParams, key: string): Pick<BoardParams, "sort" | "desc"> {
  if (p.sort === key) return { sort: key, desc: !p.desc };
  return { sort: key, desc: true };
}

export function toggleOpen(open: number[], id: number): number[] {
  return open.includes(id) ? open.filter((x) => x !== id) : [...open, id];
}

export const SUBGROUP_LABEL: Record<string, string> = { clips: "clips", tt_sounds: "TikTok sounds" };
